"use client";

import { usePageState } from "@2u-landing-pages/providers";
import { IconSizes, TypeButtonWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import EventClient from "@2u-landing-pages/utils/event-client";
import type { ButtonProps as BricksButtonProps } from "@red-digital/bricks";
import { Button as BricksButton } from "@red-digital/bricks";
import cn from "classnames";
import React, { useEffect, useState } from "react";
import { ContentfulImage } from "../ContentfulImage";
import { Icon } from "../Icon";
// @ts-ignore
import styles from "./Button.module.scss";

type ButtonProps = Omit<BricksButtonProps, "icon"> &
  Omit<TypeButtonWithoutUnresolvableLinksResponse["fields"], "name"> & {
    forceDarkLabel?: boolean;
    location?: string;
  };

const iconSizeMap = {
  small: "sm",
  medium: "md",
  large: "lg",
};

export const Button: React.FC<ButtonProps> = ({
  forceDarkLabel,
  location = "",
  icon,
  icon_mobile_only,
  children,
  action,
  onClick,
  logo,
  variation,
  link_url,
  show_dropshadow,
  ...rest
}) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia(`(max-width: 1024px)`).matches : false
  );
  const { dispatch, primaryButtonDarkLabel } = usePageState();

  const hasLogo = logo?.fields?.file?.url;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(`(max-width: 1024px`);

    const handleScreenChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener("change", handleScreenChange);

    return () => {
      mediaQuery.removeEventListener("change", handleScreenChange);
    };
  }, []);

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (action) {
      if (action === "OPEN_LINK") {
        if (link_url) window.open(link_url, "_blank");
      } else if (action === "SLIDE_TO_ELEMENT") {
        if (link_url) {
          const element = document.getElementById(link_url);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }
      } else if (action === "BACK_TO_TOP") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        dispatch({ type: action });
      }
    }
    if (onClick) onClick(event);

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location,
        position: "CTA",
        text: children?.toString() || icon || link_url || "",
      },
    });
  };

  const primaryColor = rest?.color?.includes("primary") || !rest?.color;

  return (
    <BricksButton
      className={cn(styles.button, {
        [styles.darkLabel]: (primaryButtonDarkLabel && primaryColor) || forceDarkLabel,
        [styles.dropshadow]: show_dropshadow,
        [styles.btnWithLogo]: hasLogo,
        "b-btn--icon-only": (isMobile && icon_mobile_only) || (!hasLogo && !children),
      })}
      role="button"
      aria-label={children?.toString()}
      v2
      icon={
        icon
          ? {
              component: <Icon name={icon} size={iconSizeMap[rest.size as keyof typeof iconSizeMap] as IconSizes} />,
              position: "left",
            }
          : undefined
      }
      variant={variation}
      onClick={handleOnClick}
      {...rest}
    >
      {hasLogo ? (
        <ContentfulImage image={logo} className={styles.logo} loading="lazy" />
      ) : (
        <>
          {isMobile && icon_mobile_only ? undefined : children}
          {action === "OPEN_LINK" && (
            <Icon name="external-link" style={{ fontSize: "1em" }} className={styles.externalLink} />
          )}
        </>
      )}
    </BricksButton>
  );
};
